import React, { useContext, useEffect, useRef, useState } from 'react';
import ModalContainer from '../../../../components/Modals/ModalContainer';
import styled from '@emotion/styled';
import { formatDate, truncateText } from '../../../../utils/utils';
import InfoIcon from '../../../../components/Icons/InfoIcon';
import { useAppSelector } from '../../../../redux/store';
import useGetCreatedJobPosts from '../../../../hooks/dashboard/jobs/useGetCreatedJobPosts';
import PaginationControl from '../../../../components/PaginationControl/PaginationControl';
import JobsCreatedTableSkeletonRows from '../../../../components/Skeletons/JobsCreatedTableSkeletonRows';
import { ModalContext, ModalProvider } from '../../../../context/ModalContext';
import { usePostedJobsPage } from '../../../../context/PostedJobsPageContext';
import { FIND_LOCUM } from '../../../../routes/routeConstants';
import ThreeDotVertical from '../../../../components/Icons/ThreeDotVertical';
import useInviteToApply from '../../../../hooks/dashboard/jobs/invite/useInviteToApply';
import { LoadingOutlined } from '@ant-design/icons';
import ModalTriggerContainer from '../../../../components/Modals/ModalTriggerContainer';
import { Modal } from '../../../../components/Modals/Modal';
import PostJobModal from '../../../../components/Modals/ModalsActions/PostJobModal';

const ShowModal = React.forwardRef<HTMLDivElement>((_, ref) => {
  return (
    <ModalProvider>
      <ModalTriggerContainer ref={ref} display="none">
        <></>
      </ModalTriggerContainer>
      <Modal>
        <PostJobModal />
      </Modal>
    </ModalProvider>
  );
});

const JobsCreatedTable = ({
  name,
  invitedUserId,
}: {
  name: string;
  invitedUserId: string;
}) => {
  const { authData } = useAppSelector((state) => state.auth);

  const { currentPage, setCurrentPage, clearPage } = usePostedJobsPage();

  const jobsCreatedList = useAppSelector((state) => state.jobsCreatedList);

  const modal = useContext(ModalContext);

  const [activeRowId, setActiveRowId] = useState<string | null>(null);

  const perPage = 10;

  const userId = authData?.id;

  const { getCreatedJobPosts, loading } = useGetCreatedJobPosts();

  const { loading: inviting, inviteToApply } = useInviteToApply();

  const triggerRef = useRef<HTMLDivElement>(null);

  // track that we need to fire the trigger after close
  const [shouldTrigger, setShouldTrigger] = useState(false);

  const handleClick = () => {
    // ask the modal to close
    modal?.displayModal(false);
    // mark that we want to fire the trigger once it's closed
    setShouldTrigger(true);
  };

  useEffect(() => {
    // when we've asked to trigger AND the modal is now closed...
    if (shouldTrigger && modal?.showModal === false) {
      triggerRef.current?.click();
      setShouldTrigger(false);
    }
  }, [shouldTrigger, modal?.showModal]);

  useEffect(() => {
    if (userId && modal?.showModal) {
      getCreatedJobPosts(
        {
          page: currentPage.toString(),
          perPage: perPage.toString(),
        },
        userId
      );
    }
    // eslint-disable-next-line
  }, [currentPage, userId, modal?.showModal]);

  useEffect(() => {
    return () => {
      // Clear page on unmount (only if navigating out completely, not to job details).
      if (!window.location.pathname.includes(`/${FIND_LOCUM}`)) {
        clearPage();
      }
    };
    // eslint-disable-next-line
  }, [clearPage]);

  const onInvite = async (jobId: string) => {
    inviteToApply(jobId, { invitedUserId });
  };

  return (
    <ModalContainer padding="1rem" width="80vw">
      <TableWrapper empty={jobsCreatedList.jobData.length === 0}>
        <p>
          Select Job to Invite <span>{name}</span> to apply for
        </p>
        <Info>
          <InfoIcon />
          You can only send invites for open jobs
        </Info>
        <Table empty={jobsCreatedList.jobData.length === 0}>
          {jobsCreatedList.jobData.length === 0 ? (
            <Empty>
              <p>
                You have not posted a job yet,{' '}
                <span onClick={handleClick}>make a job post now</span>
              </p>
            </Empty>
          ) : (
            <>
              {' '}
              <thead>
                <tr>
                  <Th>Job Title</Th>
                  <Th>Institution</Th>
                  <Th className="narrow">Job Type</Th>
                  <Th>Pay</Th>
                  <Th>Location</Th>
                  <Th className="narrow">Status</Th>
                  <Th className="narrow">Date Posted</Th>
                  <Th className="tiny"></Th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 10 }).map((_, i) => (
                      <JobsCreatedTableSkeletonRows key={i} />
                    ))
                  : jobsCreatedList.jobData.map((row) => (
                      <Tr key={row.id}>
                        <Td>{row.title}</Td>
                        <Td>{truncateText(row.institutionName, 3)}</Td>
                        <Td className="narrow">{row.payInterval}</Td>
                        <Td>{row.pay.formatCurrency()}</Td>
                        <Td>{truncateText(row.location, 3)}</Td>
                        <Td
                          className={`narrow ${
                            row.isFilled ? 'Closed' : 'Open'
                          }`}
                        >
                          {row.isFilled ? 'Closed' : 'Open'}
                        </Td>
                        <Td className="narrow">{formatDate(row.createdAt)}</Td>
                        <ActionTd onClick={() => setActiveRowId(row.id)}>
                          <ThreeDotVertical />
                          {activeRowId === row.id && (
                            <ActionPopup>
                              <p>
                                Invite <strong>{name}</strong> to apply for{' '}
                                <strong>{row.title}</strong> at{' '}
                                <strong>{row.institutionName}</strong>?
                              </p>
                              <ButtonGroup>
                                <Action
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveRowId(null);
                                  }}
                                  className="cancel"
                                >
                                  Cancel
                                </Action>
                                <Action
                                  onClick={(e) => {
                                    onInvite(row.id);
                                    e.stopPropagation();
                                    setActiveRowId(null);
                                  }}
                                  className="invite"
                                >
                                  {inviting ? <LoadingOutlined /> : 'Invite'}
                                </Action>
                              </ButtonGroup>
                            </ActionPopup>
                          )}
                        </ActionTd>
                      </Tr>
                    ))}
              </tbody>
            </>
          )}
        </Table>
      </TableWrapper>
      <PaginationShell>
        <PaginationControl
          endPage={
            jobsCreatedList.totalPages > 0 ? jobsCreatedList.totalPages : 1
          }
          changePage={(newPage: number) => setCurrentPage(newPage)}
          defaultNumOfPagination={5}
          currentPage={currentPage}
        />
      </PaginationShell>
      <ShowModal ref={triggerRef} />
    </ModalContainer>
  );
};

export default JobsCreatedTable;

// Styles

interface Tablee {
  empty: boolean;
}

const TableWrapper = styled.div<Tablee>`
  width: 100%;
  overflow-x: ${({ empty }) => (empty ? 'hidden' : 'auto')};
  min-height: 80dvh;

  & > p {
    font-size: 20px;
    font-weight: 400;

    & > span {
      color: ${({ theme }) => theme.palette.mainBlue};
      font-weight: 500;
    }
  }
`;

const Table = styled.table<Tablee>`
  width: 100%;
  border-collapse: collapse;
  min-width: ${({ empty }) => (empty ? '0' : '800px')};
  min-height: ${({ empty }) => (empty ? '60dvh' : '0')};
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 8px;
  border-bottom: 2px solid #ddd;
  font-weight: 500;
  text-wrap: nowrap;

  &.narrow {
    width: 110px;
  }

  &.tiny {
    width: 20px;
  }
`;

const Tr = styled.tr`
  &:nth-of-type(odd) {
    background: #fafafa;
  }

  &:hover {
    background-color: #fef4d7;
  }
`;

const Td = styled.td`
  padding: 20px 8px;
  font-size: 14px;
  font-weight: 400;

  &.Open {
    color: ${({ theme }) => theme.palette.mainPurple};
  }

  &.Closed {
    color: ${({ theme }) => theme.palette.stateColorRed};
  }

  &.Pending {
    color: ${({ theme }) => theme.palette.stateColorYellow};
  }
`;

const Info = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.greyGrey2};
  display: flex;
  gap: 10px;
  margin-top: 0.7rem;
  margin-bottomf: 0.7rem;
`;

const Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 70dvh;

  & > p {
    & > span {
      color: ${({ theme }) => theme.palette.mainBlue};
      text-decoration: underline;
      font-weight: 500;
      cursor: pointer;
    }
  }
`;

const PaginationShell = styled.div`
  display: grid;
  place-content: center;
  grid-column: span 2;

  @media (max-width: 884px) {
  }
`;

const ActionTd = styled.td`
  position: relative;
  padding: 20px 8px;
  cursor: pointer;
`;

const ActionPopup = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey2};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  border-radius: 4px;
  width: 20rem;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Action = styled.button`
  border: none;
  padding: 5px 10px;
  color: ${({ theme }) => theme.palette.greyGrey1};
  border-radius: 5px;
  cursor: pointer;

  &.invite {
    background: ${({ theme }) => theme.palette.mainBlue};
    color: ${({ theme }) => theme.palette.white};
  }

  &.cancel {
    color: ${({ theme }) => theme.palette.stateColorRed};
    background: ${({ theme }) => theme.palette.stateColorRedLight};
  }

  @media (max-width: 480px) {
  }
`;

// Mobile tweaks
// const ResponsiveStyles = styled.div`
//   @media (max-width: 600px) {
//     ${TableWrapper} {
//       /* allow horizontal scroll */
//     }
//     ${Table} {
//       font-size: 12px;
//     }
//     ${Th}, ${Td} {
//       padding: 8px 4px;
//     }
//   }
// `;
